import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { isLoadChanger } from "../../../store/mainSlice";
import { selectPartner } from "../../../store/partnerStore";
import { selectClientAddress } from "../../../store/userSlice";
import { resolvePartnerSync } from "../../../utils/partner";
import { isV2Enabled, V2_SCREEN_KEYS } from "../../registry";
import { commitGpsFix } from "../../utils/address";
import { GPS_FLOW_ENABLED, requestLocation } from "../../utils/resolveLocation";
import "./locationRequired.scss";

// Placeholder for the pin illustration from the design — a flat stand-in until the
// exported asset lands, at which point this becomes an <img src={pinArt} />.
const PinArt = () => (
  <svg className="lr-v2-art" viewBox="0 0 160 190" role="presentation" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="80" cy="176" rx="34" ry="9" fill="#DDDCF3" />
    <path
      d="M80 4c-27.6 0-50 22.4-50 50 0 34.7 41.3 74.8 47.1 110.2a3 3 0 0 0 5.8 0C88.7 128.8 130 88.7 130 54c0-27.6-22.4-50-50-50Z"
      fill="#E5147F"
    />
    <circle cx="80" cy="52" r="19" fill="#FFFFFF" />
  </svg>
);

// The arrow that sits beside the CTA label in the design. In RTL it lands to the
// left of the text, which is why it follows the label in DOM order.
const NavArrow = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 1 1 6.2l5 1.8 1.8 5L13 1Z" fill="currentColor" />
  </svg>
);

// V2 location gate (/location-required). Shown when a location prompt the app
// itself raised came back with nothing *and* we have no coordinates on file — a saved
// address or the profile address on the Login response both count, so anyone who
// has already told us where they are never sees this. Two entries: the welcome
// screen's CTA (first onboarding) and home's entry attempt (useEntryGpsRefresh,
// for an existing user who skipped welcome and denies the prompt on home).
//
// The X is a real escape hatch: home tolerates missing coordinates
// (GetCarousels simply omits Latitude/Longitude and the distance chips drop out),
// and a past denial never re-raises the gate — only a fresh failed attempt does.
function LocationRequired() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storePartner = useSelector(selectPartner);
  const clientAddress = useSelector(selectClientAddress);
  const mainUserData = useSelector((state) => state.userSlice.mainUserData);

  // Not the bare selector: this screen can be reached mid-handshake, before
  // silentLogin has dispatched setPartner, when the store still reads NONE.
  const partner = resolvePartnerSync(storePartner);

  const retryLocation = async () => {
    dispatch(isLoadChanger(true));
    const result = await requestLocation();
    dispatch(isLoadChanger(false));

    // Still refused (a blocked origin errors instantly, without re-prompting) —
    // the button must still move the user forward: home tolerates missing
    // coordinates, same as the X. Re-enabling location for real happens in the
    // device settings, which a web page cannot open without a host-app bridge.
    if (!result.coords) {
      navigate("/home", { replace: true });
      return;
    }

    // Fire-and-forget: the coordinates land in the store synchronously (so home
    // ranks by them right away), the address words follow in the background.
    commitGpsFix({ coords: result.coords, clientAddress, mainUserData, dispatch });
    navigate("/home", { replace: true });
  };

  // returnTo, because AddressSelect's default is navigate(-1) — which would land
  // the user back on this screen instead of moving them on.
  const enterAddress = () => navigate("/address-select", { state: { returnTo: "/home" } });

  // With the GPS flow disabled nothing navigates here, but a direct URL hit or a
  // stale history entry must bounce too.
  if (!GPS_FLOW_ENABLED || !isV2Enabled(V2_SCREEN_KEYS.LOCATION_REQUIRED, partner)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="lr-v2">
      <button
        type="button"
        className="lr-v2-close"
        onClick={() => navigate("/home", { replace: true })}
        aria-label="close"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 2l16 16M18 2L2 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </button>

      <div className="lr-v2-content">
        <PinArt />
        <h1 className="lr-v2-title">{t("LOCATION_REQUIRED_TITLE")}</h1>
        <p className="lr-v2-body">{t("LOCATION_REQUIRED_BODY")}</p>
      </div>

      <div className="lr-v2-actions">
        <button type="button" className="lr-v2-cta" onClick={retryLocation}>
          <span>{t("LOCATION_REQUIRED_CTA")}</span>
          <NavArrow />
        </button>
        <button type="button" className="lr-v2-manual" onClick={enterAddress}>
          {t("LOCATION_REQUIRED_MANUAL")}
        </button>
      </div>
    </div>
  );
}

export default LocationRequired;
